import {
  AccessContextApiError,
  getAccessContext,
  type AccessContextResponse,
} from '@urbanik/api-client';
import { StatePanel, canonicalStates } from '@urbanik/ui';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiBase } from './api-base';

const AccessContext = createContext<AccessContextResponse | null>(null);

export function useAccess(): AccessContextResponse {
  const access = useContext(AccessContext);
  if (!access) throw new Error('useAccess requiere AccessBoundary.');
  return access;
}

export function AccessBoundary({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<AccessContextResponse | null>(null);
  const [state, setState] = useState<'loading' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getAccessContext(apiBase, controller.signal)
      .then(setAccess)
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof AccessContextApiError && error.kind === 'unauthorized') {
          if (!window.location.hash.startsWith('#login')) window.location.hash = '#login';
          setState('loading');
          return;
        }
        setState('error');
      });
    return () => controller.abort();
  }, [attempt]);
  useEffect(() => {
    const refresh = () => setAttempt((value) => value + 1);
    window.addEventListener('hashchange', refresh);
    return () => window.removeEventListener('hashchange', refresh);
  }, []);
  if (!access) {
    const definition = canonicalStates.find((item) => item.kind === state)!;
    return (
      <main className="access-boundary">
        <StatePanel state={definition} onAction={() => setAttempt((value) => value + 1)} />
      </main>
    );
  }
  return <AccessContext.Provider value={access}>{children}</AccessContext.Provider>;
}
